import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { VendorService } from '@/services/vendor.service';
import { InvoiceStatus } from '@/models/vendor.model';
import { Button, Card, Badge, LoadingSpinner, EmptyState } from '@/components/common';
import { FileText, ArrowLeft, Edit, CheckCircle2, XCircle, DollarSign, Calendar, CreditCard } from 'lucide-react';

export const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['vendorInvoice', id],
    queryFn: () => (id ? VendorService.getInvoiceById(id) : null),
    enabled: Boolean(id),
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<{ status: InvoiceStatus; amountPaid: number; balance: number; paymentDate: Date | null }>) => {
      if (!id) return;
      await VendorService.updateInvoice(id, updates as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendorInvoice', id] });
      queryClient.invalidateQueries({ queryKey: ['vendorInvoices'] });
    },
  });

  const getStatusVariant = (status: InvoiceStatus): "success" | "warning" | "danger" | "gray" | "info" => {
    const variants: Record<string, "success" | "warning" | "danger" | "gray" | "info"> = {
      [InvoiceStatus.pending]: 'warning',
      [InvoiceStatus.approved]: 'info',
      [InvoiceStatus.overdue]: 'danger',
      [InvoiceStatus.paid]: 'success',
      [InvoiceStatus.cancelled]: 'gray',
    };
    return variants[status] || 'gray';
  };

  if (isLoading) return <LoadingSpinner text="Loading invoice..." fullScreen />;

  if (error || !invoice) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={FileText}
          title={error ? "Failed to load invoice" : "Invoice not found"}
          description={error instanceof Error ? error.message : "The requested invoice does not exist."}
          action={
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/vendors')}>
              Back to Vendors
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => navigate('/vendors')}
          className="mb-4"
        >
          Back to Vendors
        </Button>
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4 flex-1">
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900">Invoice {invoice.invoiceNumber || invoice.id}</h1>
                  <Badge variant={getStatusVariant(invoice.status)} size="md">
                    {invoice.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                  <span>Vendor: <Link to={`/vendors/${invoice.vendorId}`} className="text-blue-600 hover:text-blue-800">{invoice.vendorName}</Link></span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {invoice.invoiceDate.toLocaleDateString()}
                  </span>
                  <span>Due: {invoice.dueDate.toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={Edit}
              onClick={() => navigate(`/vendors/invoices/${invoice.id}/edit`)}
            >
              Edit
            </Button>
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-6 pb-6 border-b border-gray-200">
          {invoice.status === InvoiceStatus.pending && (
            <Button
              variant="success"
              size="sm"
              icon={CheckCircle2}
              onClick={() => updateMutation.mutate({ status: InvoiceStatus.approved })}
              isLoading={updateMutation.isPending}
            >
              Approve
            </Button>
          )}
          {(invoice.status === InvoiceStatus.pending || invoice.status === InvoiceStatus.approved || invoice.status === InvoiceStatus.overdue) && (
            <Button
              variant="primary"
              size="sm"
              icon={DollarSign}
              onClick={() =>
                updateMutation.mutate({
                  status: InvoiceStatus.paid,
                  amountPaid: invoice.total,
                  balance: 0,
                  paymentDate: invoice.paymentDate || new Date(),
                })
              }
              isLoading={updateMutation.isPending}
            >
              Mark paid
            </Button>
          )}
          {(invoice.status === InvoiceStatus.pending || invoice.status === InvoiceStatus.approved) && (
            <Button
              variant="danger"
              size="sm"
              icon={XCircle}
              onClick={() => updateMutation.mutate({ status: InvoiceStatus.overdue })}
              isLoading={updateMutation.isPending}
            >
              Mark overdue
            </Button>
          )}
          {invoice.status !== InvoiceStatus.cancelled && (
            <Button
              variant="outline"
              size="sm"
              icon={XCircle}
              onClick={() => {
                const ok = window.confirm('Cancel this invoice?');
                if (ok) updateMutation.mutate({ status: InvoiceStatus.cancelled });
              }}
              isLoading={updateMutation.isPending}
            >
              Cancel
            </Button>
          )}
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Qty</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Unit</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoice.items.map((i) => (
                <tr key={i.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{i.description}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{i.category || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">{i.quantity}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">${i.unitPrice.toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">${i.total.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="text-sm font-medium text-gray-600 mb-1">Subtotal</div>
            <div className="text-2xl font-bold text-gray-900">${invoice.subtotal.toLocaleString()}</div>
          </Card>
          <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="text-sm font-medium text-gray-600 mb-1">Tax</div>
            <div className="text-2xl font-bold text-gray-900">${invoice.tax.toLocaleString()}</div>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
            <div className="text-sm font-medium text-blue-600 mb-1">Total</div>
            <div className="text-2xl font-bold text-blue-900">${invoice.total.toLocaleString()}</div>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100">
            <div className="text-sm font-medium text-green-600 mb-1">Balance</div>
            <div className="text-2xl font-bold text-green-900">${invoice.balance.toLocaleString()}</div>
          </Card>
        </div>

        {(invoice.paymentMethod || invoice.paymentDate) && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-sm font-medium text-gray-600 mb-1 flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Payment
            </div>
            <div className="text-gray-900">
              {invoice.paymentMethod ? invoice.paymentMethod : '—'}
              {invoice.paymentDate ? ` • ${invoice.paymentDate.toLocaleDateString()}` : ''}
            </div>
          </div>
        )}

        {invoice.notes ? (
          <div className="mt-6">
            <div className="text-sm font-medium text-gray-600 mb-2">Notes</div>
            <div className="text-sm text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">{invoice.notes}</div>
          </div>
        ) : null}
      </Card>
    </div>
  );
};

